/**
 * Pagination — Sprint-16-Brand.
 *
 * Page-Number-Pills mit navy-Active-Fill (matches SegmentedControl-Semantik:
 * navy = aktive Auswahl). Prev/Next als Ghost-Icon-Buttons (direct lucide).
 */
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './Button';
import { cn } from '@/shared/lib/cn';

export interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}

export function Pagination({ page, pageSize, total, onPageChange }: PaginationProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  const pages = new Set<number>([1, totalPages, page, page - 1, page + 1]);
  const visiblePages = [...pages].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-1 py-3">
      <p className="text-ink-faint text-xs">
        Zeige <span className="text-ink">{from}</span>–<span className="text-ink">{to}</span> von{' '}
        <span className="text-ink">{total}</span>
      </p>

      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          aria-label="Vorherige Seite"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          <ChevronLeft size={16} strokeWidth={2} />
        </Button>

        {visiblePages.map((p, idx) => {
          const prev = visiblePages[idx - 1];
          const showGap = prev !== undefined && p - prev > 1;
          return (
            <span key={p} className="flex items-center gap-1">
              {showGap && <span className="text-ink-faint px-1">…</span>}
              <button
                type="button"
                onClick={() => onPageChange(p)}
                className={cn(
                  'rounded-full px-3 py-1.5 text-xs font-medium transition-colors',
                  p === page
                    ? 'bg-navy text-white'
                    : 'text-ink-soft border-line/50 hover:bg-card-alt border',
                )}
                aria-current={p === page ? 'page' : undefined}
              >
                {p}
              </button>
            </span>
          );
        })}

        <Button
          variant="ghost"
          size="icon"
          aria-label="Nächste Seite"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
        >
          <ChevronRight size={16} strokeWidth={2} />
        </Button>
      </div>
    </div>
  );
}
